/**
 * URL处理模块
 * @author luoying
 * @since 17/09/15
 */
/**
 * 解析URL
 * @param  {string} url url
 * @return {Object}     返回解析完毕的对象
 * @example
 * url: `https://www.rabbitpre.com:8080/m/xyz/index.html?abc=1#mobile=1`
 * -->
 * protocol: 'https',
 * host: 'www.rabbitpre.com',
 * port: '8080',
 * hostWidthPort: 'www.rabbitpre.com:8080',
 * domain: 'rabbitpre.com',
 * path: '/m/xyz/index.html',
 * directory: '/m/xyz/',
 * file: 'index.html',
 * query: 'abc=1',
 * hash: 'mobile=1',
 * relative: '/m/xyz/index.html?abc=1#mobile=1',
 * isHttps: true,
 * isHttp: true,
 * isEMail: false
 */
export function parse(url) {
    const ret = {
        source: url,
        protocol: '',
        host: '',
        port: '',
        hostWidthPort: '',
        basename: '',
        domain: '',
        path: '',
        directory: '',
        file: '',
        query: '',
        hash: '',
        relative: '',
        userInfo: '',
        user: '',
        password: '',
        ipv4: '',
        ipv6: '',
        isHttp: false,
        isHttps: false,
        isEMail: false,
    };
    if (!url) {
        return ret;
    }
    const keys = [
        'source',
        'protocol',
        'hostWidthPort',
        'userInfo',
        'user',
        'password',
        'host',
        'ipv4',
        'ipv6',
        'basename',
        'domain',
        'port',
        'relative',
        'path',
        'directory',
        'file',
        'query',
        'hash',
    ];
    const reg = /^(?:(?![^:@]+:[^:@/]*@)([^[:/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?((?:(\d+\.\d+\.\d+\.\d+)|\[([a-fA-F0-9:]+)\]|([^.:/?#]*))(?:\.([^:/?#]*))?)(?::(\d*))?)(((\/(?:[^?#](?![^?#/]*\.[^?#/.]+(?:[?#]|$)))*\/?)?([^?#/]*))(?:\?([^#]*))?(?:#(.*))?)/;
    const m = reg.exec(url) || [];
    const uri = {};
    let i = 18;
    while (i--) {
        uri[keys[i]] = m[i];
    }
    for (const key of keys) {
        ret[key] = uri[key] || '';
    }
    ret.isHttp =
        ret.protocol === 'http' ||
            ret.protocol === 'https' ||
            ret.protocol === 'rtmp' ||
            ret.protocol === 'rtmps';
    ret.isHttps = ret.protocol === 'https';
    ret.isEMail = ret.protocol === 'mailto';
    return ret;
}
/**
 * 把链接显式声明为https协议
 * @param {string} url 需要转换的URL
 * @returns {string}
 */
export function getUrlWithHttpsProtocol(url) {
    const isCommon = /^\/\/.*$/;
    const isHttp = /^http:\/\/.*$/;
    const isHttps = /^https:\/\/.*$/;
    // 情况1：
    if (!url) {
        return '';
    }
    // 情况2：
    // URL=https://xxx.xxx.xx
    if (isHttps.test(url)) {
        return url;
    }
    // 情况3：
    // URL=http://xxx.xxx.xx
    if (isHttp.test(url)) {
        return url.replace('http', 'https');
    }
    // 情况4：
    // URL=(//):xxx.xxx.xx
    if (isCommon.test(url)) {
        return `https:${url}`;
    }
    // 不匹配的情况，补充`https://`协议
    return `https://${url}`;
}
/**
 * 返回包含协议的完整url
 * @param  {string} url 原始url，有可能不包含协议
 * @param [string] [protocol] 当url缺失协议时，以该特定协议来补充，否则以当前页面的协议来补充
 * @return {string}     包含协议的完整url
 */
export function getProtocolUrl(url, protocol) {
    const reg = /^(?:http:|https:)/i;
    if (reg.test(url))
        return url;
    if (!protocol)
        protocol = window.location.protocol;
    return protocol + (/^\/\//.test(url) ? url : '//' + url);
}
/**
 * 获取代理地址
 * 本地开发环境，绕过图片等资源的防盗链机制
 * 生产环境，直接到达CDN服务器
 * @param {string} url 源地址
 * @param {boolean} [isProduction] 是否是生产环境
 * @returns {string} 返回代理地址
 */
export function getProxyUrl(url, isProduction) {
    // 本地开发环境，通过server代理CDN资源请求：绕过防盗链机制
    if (process.env.NODE_ENV === 'development' && !isProduction) {
        const host = encodeURIComponent(getProtocolUrl(url));
        return `${process.env.PROXY_URL_PREFIX || ''}/proxy?host=${host}`;
    }
    return url;
}
/**
 * 是否本站URL
 *
 * @export
 * @param {string} url 源地址
 */
export function isSameSiteUrl(url) {
    if (['http://', 'https://', '//'].some(prefix => url.startsWith(prefix))) {
        const { host: targetHost } = parse(url);
        const { host: originalHost } = parse(window.location.href);
        if (targetHost !== originalHost)
            return false;
    }
    return true;
}
//# sourceMappingURL=url.js.map